/**
 * Energy cost burden percentile for the Overview “Burdens” card (Built Environment → Energy Cost Burden).
 *
 * 1) Prefer LEAD-derived `lead_energy_cost_burden_ranked` (AMI, 0-30% band), percentile-ranked within peer geography.
 * 2) Else `energy_cost_burden_percentile` when it exists and has a matching row.
 * 3) Else `livability_index_precalculated.housing_burden_percentile` (same pipeline as Livability Over Time).
 * 4) Else CalEnviroScreen `housing_burden_percentile` (housing + utilities burden proxy).
 *
 * When (1) only has county rows, cities are resolved to their parent county.
 */

import { supabaseClient } from './supabase';
import { supabaseLocationService } from './supabaseLocationService';

const TABLE = 'energy_cost_burden_percentile';
const LEAD_RANKED_VIEW = 'lead_energy_cost_burden_ranked';
const LEAD_DEFAULT_FRAMEWORK = 'AMI';
const LEAD_DEFAULT_INCOME_BAND = '0-30%';
const LEAD_DEFAULT_YEAR = 2022;

function toNum(v: unknown): number | null {
  if (v === null || v === undefined) return null;
  const n = typeof v === 'number' ? v : parseFloat(String(v).replace(/,/g, ''));
  return Number.isFinite(n) ? n : null;
}

function asRow(x: unknown): Record<string, unknown> | undefined {
  if (x != null && typeof x === 'object' && !Array.isArray(x)) return x as Record<string, unknown>;
  return undefined;
}

function geofipsVariants(gf: number | string | null | undefined): string[] {
  if (gf === null || gf === undefined || gf === '') return [];
  const s = String(gf).trim();
  const out = new Set<string>([s]);
  if (/^\d+$/.test(s)) {
    const unpadded = s.replace(/^0+/, '') || '0';
    out.add(unpadded);
    if (s.length === 6) out.add(`0${s}`);
    if (s.length === 5 && /^\d+$/.test(s)) out.add(s.padStart(7, '0'));
    if (s.length === 7) out.add(s);
  }
  return Array.from(out);
}

/** Column with the 0–100 (or 0–1) percentile value. */
function pickValueColumn(sample: Record<string, unknown>): string | null {
  const preferred = [
    'energy_cost_burden_percentile',
    'percentile',
    'percentile_value',
    'burden_percentile',
    'energy_burden_percentile',
    'energy_burden_pct_income',
    'value',
  ];
  // Prefer known columns if they exist — first row may be null but other rows have values.
  for (const k of preferred) {
    if (k in sample) return k;
  }
  const skip = new Set([
    'year',
    'geofips',
    'geoid',
    'GEOID',
    'location_name',
    'geography_type',
    'id',
    'created_at',
    'updated_at',
  ]);
  for (const [k, v] of Object.entries(sample)) {
    if (skip.has(k)) continue;
    if (toNum(v) != null) return k;
  }
  return null;
}

/** GEOID / geofips column names used in `energy_cost_burden_percentile` (varies by ETL). */
function geoIdColumnsFromSample(sample: Record<string, unknown>): string[] {
  const out: string[] = [];
  if ('geofips' in sample) out.push('geofips');
  if ('geoid' in sample) out.push('geoid');
  if ('GEOID' in sample) out.push('GEOID');
  return out.length ? out : ['geofips'];
}

/** geography_type values in DB (lowercase place/county is common in energy ETL). */
const GEO_TYPES_LOOKUP = [
  'place',
  'Place',
  'city',
  'City',
  'County',
  'county',
  'state',
  'State',
];

/** Parent county string from `population` (City row) when the in-memory city→county map is empty or not ready. */
async function getCountyNameForCityFromPopulation(cityName: string): Promise<string | null> {
  const trimmed = cityName.trim();
  if (!trimmed) return null;
  const cityPlaceTypes = ['City', 'Place', 'place'];
  let { data, error } = await supabaseClient
    .from('population')
    .select('county')
    .eq('location_name', trimmed)
    .in('geography_type', cityPlaceTypes)
    .not('county', 'is', null)
    .limit(1);
  if (!error && !data?.length) {
    const prefix = await supabaseClient
      .from('population')
      .select('county')
      .ilike('location_name', `${trimmed}%`)
      .in('geography_type', cityPlaceTypes)
      .not('county', 'is', null)
      .limit(1);
    data = prefix.data;
    error = prefix.error;
  }
  if (error || !data?.length) return null;
  const c = data[0]?.county;
  return typeof c === 'string' && c.trim() ? c.trim() : null;
}

/** County geofips from `population` for matching `energy_cost_burden_percentile` county rows. */
async function getCountyGeofipsFromPopulation(countyNameFromMap: string): Promise<number | string | null> {
  const base = countyNameFromMap.replace(/\s+County$/i, '').trim();
  const nameVariants = Array.from(
    new Set([countyNameFromMap.trim(), `${base} County`, base].filter(Boolean))
  );
  for (const gt of ['County', 'county'] as const) {
    for (const name of nameVariants) {
      const { data } = await supabaseClient
        .from('population')
        .select('geofips')
        .eq('geography_type', gt)
        .eq('location_name', name)
        .limit(1);
      const gf = data?.[0]?.geofips;
      if (gf != null) return gf;
    }
  }
  return null;
}

type LookupCtx = {
  valueKey: string;
  hasYearColumn: boolean;
  readRow: (data: unknown) => number | null;
  years: number[];
};

function queryByGeoColumn(col: string, val: string | number) {
  const base = supabaseClient.from(TABLE).select('*');
  if (col === 'geofips') return base.eq('geofips', val);
  if (col === 'geoid') return base.eq('geoid', val);
  return base.eq('GEOID', val);
}

function runGeofipsAndNameQueries(
  ctx: LookupCtx,
  loc: string,
  geofips: number | string | null,
  geoTypes: string[],
  geoCols: string[]
): Promise<number | null> {
  const { hasYearColumn, readRow, years } = ctx;

  return (async () => {
    for (const v of geofipsVariants(geofips)) {
      for (const col of geoCols) {
        if (hasYearColumn) {
          for (const y of years) {
            const { data } = await queryByGeoColumn(col, v).eq('year', y).limit(1);
            const val = readRow(data);
            if (val != null) return val;
            const { data: n2 } = await queryByGeoColumn(col, Number(v)).eq('year', y).limit(1);
            const val2 = readRow(n2);
            if (val2 != null) return val2;
          }
          const { data: latestGf } = await queryByGeoColumn(col, v)
            .order('year', { ascending: false })
            .limit(1);
          const vg = readRow(latestGf);
          if (vg != null) return vg;
        } else {
          const { data: oneGf } = await queryByGeoColumn(col, v).limit(1);
          const g1 = readRow(oneGf);
          if (g1 != null) return g1;
          const { data: oneGf2 } = await queryByGeoColumn(col, Number(v)).limit(1);
          const g2 = readRow(oneGf2);
          if (g2 != null) return g2;
        }
      }
    }

    if (hasYearColumn) {
      for (const y of years) {
        for (const gt of geoTypes) {
          for (const usePrefix of [false, true]) {
            let q = supabaseClient.from(TABLE).select('*').eq('geography_type', gt).eq('year', y).limit(1);
            q = usePrefix ? q.ilike('location_name', `${loc}%`) : q.eq('location_name', loc);
            const { data, error } = await q;
            if (error) continue;
            const val = readRow(data);
            if (val != null) return val;
          }
        }
      }
      for (const gt of geoTypes) {
        for (const usePrefix of [false, true]) {
          let q = supabaseClient
            .from(TABLE)
            .select('*')
            .eq('geography_type', gt)
            .order('year', { ascending: false })
            .limit(1);
          q = usePrefix ? q.ilike('location_name', `${loc}%`) : q.eq('location_name', loc);
          const { data } = await q;
          const val = readRow(data);
          if (val != null) return val;
        }
      }
    } else {
      for (const gt of geoTypes) {
        for (const usePrefix of [false, true]) {
          let q = supabaseClient.from(TABLE).select('*').eq('geography_type', gt).limit(1);
          q = usePrefix ? q.ilike('location_name', `${loc}%`) : q.eq('location_name', loc);
          const { data, error } = await q;
          if (error) continue;
          const val = readRow(data);
          if (val != null) return val;
        }
      }
    }

    // geography_type may not match (e.g. CDP, township); still match by place/county name.
    if (hasYearColumn) {
      for (const y of years) {
        const { data } = await supabaseClient
          .from(TABLE)
          .select('*')
          .eq('year', y)
          .ilike('location_name', loc)
          .limit(1);
        const val = readRow(data);
        if (val != null) return val;
      }
    } else {
      const { data } = await supabaseClient.from(TABLE).select('*').ilike('location_name', loc).limit(1);
      const val = readRow(data);
      if (val != null) return val;
    }

    return null;
  })();
}

async function fetchPercentileFromLeadRankedView(
  locationName: string,
  geofips: number | string | null
): Promise<number | null> {
  // Return null if view is missing/empty so normal fallbacks continue.
  const { data: sample, error: sampleErr } = await supabaseClient.from(LEAD_RANKED_VIEW).select('geofips').limit(1);
  if (sampleErr || !sample?.length) return null;

  const locType = supabaseLocationService.getLocationType(locationName);
  const geoTypes: string[] =
    locType === 'county' ? ['county'] : locType === 'city' ? ['place'] : ['place', 'county'];
  const geoAliases = (gt: string): string[] => {
    if (gt === 'place') return ['place', 'Place', 'city', 'City'];
    if (gt === 'county') return ['county', 'County'];
    return [gt];
  };

  const locateByGeofips = async (): Promise<number | null> => {
    for (const gt of geoTypes) {
      for (const gf of geofipsVariants(geofips)) {
        const { data, error } = await supabaseClient
          .from(LEAD_RANKED_VIEW)
          .select('energy_burden_percentile')
          .eq('year', LEAD_DEFAULT_YEAR)
          .eq('framework', LEAD_DEFAULT_FRAMEWORK)
          .eq('income_band', LEAD_DEFAULT_INCOME_BAND)
          .in('geography_type', geoAliases(gt))
          .eq('geofips', gf)
          .limit(1);
        if (error) continue;
        const v = toNum(data?.[0]?.energy_burden_percentile);
        if (v != null) return v;
      }
    }
    return null;
  };

  const locateByName = async (): Promise<number | null> => {
    const loc = locationName.trim();
    if (!loc) return null;
    for (const gt of geoTypes) {
      for (const usePrefix of [false, true]) {
        let q = supabaseClient
          .from(LEAD_RANKED_VIEW)
          .select('energy_burden_percentile')
          .eq('year', LEAD_DEFAULT_YEAR)
          .eq('framework', LEAD_DEFAULT_FRAMEWORK)
          .eq('income_band', LEAD_DEFAULT_INCOME_BAND)
          .in('geography_type', geoAliases(gt))
          .limit(1);
        q = usePrefix ? q.ilike('location_name', `${loc}%`) : q.eq('location_name', loc);
        const { data, error } = await q;
        if (error) continue;
        const v = toNum(data?.[0]?.energy_burden_percentile);
        if (v != null) return v;
      }
    }
    return null;
  };

  const byGeofips = await locateByGeofips();
  if (byGeofips != null) return byGeofips;
  return locateByName();
}

function percentileRank(values: number[], target: number): number | null {
  if (!values.length) return null;
  if (values.length === 1) return 50;
  const sorted = [...values].sort((a, b) => a - b);
  const first = sorted.indexOf(target);
  const last = sorted.lastIndexOf(target);
  if (first < 0) return null;
  const avgRank = (first + last) / 2;
  return (avgRank / (sorted.length - 1)) * 100;
}

// Fallback if ranked SQL view is unavailable: compute percentile client-side from LEAD table.
async function fetchPercentileFromLeadAllFrameworksComputed(
  locationName: string,
  geofips: number | string | null
): Promise<number | null> {
  const { data: sample, error: sampleErr } = await supabaseClient.from('lead_energy_cost_burden_all_frameworks').select('geofips').limit(1);
  if (sampleErr || !sample?.length) return null;

  const locType = supabaseLocationService.getLocationType(locationName);
  const geoTypes: string[] =
    locType === 'county' ? ['county'] : locType === 'city' ? ['place'] : ['place', 'county'];
  const geoAliases = (gt: string): string[] => {
    if (gt === 'place') return ['place', 'Place', 'city', 'City'];
    if (gt === 'county') return ['county', 'County'];
    return [gt];
  };

  const locateByGeofips = async (): Promise<{ geographyType: string; value: number } | null> => {
    for (const gt of geoTypes) {
      for (const gf of geofipsVariants(geofips)) {
        const { data, error } = await supabaseClient
          .from('lead_energy_cost_burden_all_frameworks')
          .select('energy_burden_pct_income')
          .eq('year', LEAD_DEFAULT_YEAR)
          .eq('framework', LEAD_DEFAULT_FRAMEWORK)
          .eq('income_band', LEAD_DEFAULT_INCOME_BAND)
          .in('geography_type', geoAliases(gt))
          .eq('geofips', gf)
          .limit(1);
        if (error) continue;
        const v = toNum(data?.[0]?.energy_burden_pct_income);
        if (v != null) return { geographyType: gt, value: v };
      }
    }
    return null;
  };

  const locateByName = async (): Promise<{ geographyType: string; value: number } | null> => {
    const loc = locationName.trim();
    if (!loc) return null;
    for (const gt of geoTypes) {
      for (const usePrefix of [false, true]) {
        let q = supabaseClient
          .from('lead_energy_cost_burden_all_frameworks')
          .select('energy_burden_pct_income')
          .eq('year', LEAD_DEFAULT_YEAR)
          .eq('framework', LEAD_DEFAULT_FRAMEWORK)
          .eq('income_band', LEAD_DEFAULT_INCOME_BAND)
          .in('geography_type', geoAliases(gt))
          .limit(1);
        q = usePrefix ? q.ilike('location_name', `${loc}%`) : q.eq('location_name', loc);
        const { data, error } = await q;
        if (error) continue;
        const v = toNum(data?.[0]?.energy_burden_pct_income);
        if (v != null) return { geographyType: gt, value: v };
      }
    }
    return null;
  };

  let located = await locateByGeofips();
  if (!located) located = await locateByName();
  if (!located) return null;

  const { data: peers, error: peerErr } = await supabaseClient
    .from('lead_energy_cost_burden_all_frameworks')
    .select('energy_burden_pct_income')
    .eq('year', LEAD_DEFAULT_YEAR)
    .eq('framework', LEAD_DEFAULT_FRAMEWORK)
    .eq('income_band', LEAD_DEFAULT_INCOME_BAND)
    .in('geography_type', geoAliases(located.geographyType))
    .not('energy_burden_pct_income', 'is', null);
  if (peerErr || !peers?.length) return null;

  const values = peers
    .map((r: { energy_burden_pct_income?: number | string | null }) => toNum(r.energy_burden_pct_income))
    .filter((v: number | null): v is number => v != null);

  return percentileRank(values, located.value);
}

/** Matches Overview livability trend query (`City` / `County` + `year`). */
async function fetchHousingBurdenPercentileFromLivability(
  locationName: string,
  geofips: number | string | null
): Promise<number | null> {
  const loc = locationName.trim();
  if (!loc) return null;

  const locType = supabaseLocationService.getLocationType(locationName);
  const geoTypesOrdered =
    locType === 'county'
      ? ['County', 'county']
      : locType === 'city'
        ? ['City', 'Place', 'place']
        : ['City', 'County', 'Place', 'place', 'county'];

  for (const gt of geoTypesOrdered) {
    const { data, error } = await supabaseClient
      .from('livability_index_precalculated')
      .select('housing_burden_percentile, year')
      .eq('location_name', loc)
      .eq('geography_type', gt)
      .not('housing_burden_percentile', 'is', null)
      .order('year', { ascending: false })
      .limit(1);
    if (error) continue;
    const p = toNum(data?.[0]?.housing_burden_percentile);
    if (p != null) return p;
  }

  const variants = geofipsVariants(geofips);
  for (const gf of variants) {
    const { data, error } = await supabaseClient
      .from('livability_index_precalculated')
      .select('housing_burden_percentile, year')
      .eq('geofips', gf)
      .not('housing_burden_percentile', 'is', null)
      .order('year', { ascending: false })
      .limit(1);
    if (error) continue;
    const p = toNum(data?.[0]?.housing_burden_percentile);
    if (p != null) return p;
  }

  return null;
}

/** CalEnviroScreen 4.0 housing + utilities burden percentile (peer-ranked). */
async function fetchHousingBurdenPercentileFromCalEnviro(
  locationName: string,
  geofips: number | string | null
): Promise<number | null> {
  const cesGeoTypes = ['City', 'County', 'place'];

  const tryRead = async (gf: string | number, geographyType: string): Promise<number | null> => {
    const { data, error } = await supabaseClient
      .from('calenviroscreen_4_0_aggregated')
      .select('housing_burden_percentile')
      .eq('geofips', String(gf))
      .eq('geography_type', geographyType)
      .limit(1);
    if (error) return null;
    return toNum(data?.[0]?.housing_burden_percentile);
  };

  const tryAllGeo = async (gf: string | number): Promise<number | null> => {
    for (const g of cesGeoTypes) {
      const p = await tryRead(gf, g);
      if (p != null) return p;
    }
    return null;
  };

  for (const v of geofipsVariants(geofips)) {
    const p = await tryAllGeo(v);
    if (p != null) return p;
  }

  const gfFromService = await supabaseLocationService.getGeofips(locationName);
  if (gfFromService != null) {
    const p = await tryAllGeo(gfFromService);
    if (p != null) return p;
  }

  return null;
}

async function fetchFromDedicatedEnergyCostBurdenTable(
  locationName: string,
  geofips: number | string | null
): Promise<number | null> {
  const { data: sample, error: e0 } = await supabaseClient.from(TABLE).select('*').limit(1);
  if (e0 || !sample?.length) return null;

  const first = asRow(sample[0]);
  if (!first) return null;

  const valueKey = pickValueColumn(first);
  if (!valueKey) return null;

  const hasYearColumn = 'year' in first;
  const years = [2023, 2022, 2021, 2020, 2019, 2018, 2017, 2016];

  const readRow = (data: unknown): number | null => {
    const row = Array.isArray(data) ? asRow(data[0]) : undefined;
    return row ? toNum(row[valueKey]) : null;
  };

  const ctx: LookupCtx = { valueKey, hasYearColumn, readRow, years };

  const loc = locationName.trim();
  const geoCols = geoIdColumnsFromSample(first);
  const geoMeta = await supabaseLocationService.getGeofipsWithGeographyType(locationName);
  const geoTypesPrimary = Array.from(
    new Set([...(geoMeta?.geography_type ? [geoMeta.geography_type] : []), ...GEO_TYPES_LOOKUP])
  );

  let result = await runGeofipsAndNameQueries(ctx, loc, geofips, geoTypesPrimary, geoCols);
  if (result != null) return result;

  const locType = supabaseLocationService.getLocationType(locationName);
  const shouldTryCountyFallback = locType === 'city' || locType === null;
  if (shouldTryCountyFallback) {
    const countyName =
      supabaseLocationService.getCountyForCity(loc) ?? (await getCountyNameForCityFromPopulation(loc));
    if (countyName) {
      const countyGeofips = await getCountyGeofipsFromPopulation(countyName);
      const base = countyName.replace(/\s+County$/i, '').trim();
      const countyLabels = Array.from(new Set([countyName.trim(), `${base} County`, base].filter(Boolean)));

      const countyGeoTypes = ['County', 'county'];
      for (const countyLabel of countyLabels) {
        result = await runGeofipsAndNameQueries(ctx, countyLabel, countyGeofips, countyGeoTypes, geoCols);
        if (result != null) return result;
      }
    }
  }

  return null;
}

/**
 * Percentile for Energy Cost Burden on the Overview card.
 * Tries LEAD ranked view first (or computes from LEAD base table if the view is unavailable), then dedicated table, then livability (same as Overview housing composite), then CalEnviroScreen.
 */
export async function fetchEnergyCostBurdenPercentile(
  locationName: string,
  geofips: number | string | null
): Promise<number | null> {
  if (!locationName?.trim()) return null;

  await supabaseLocationService.initialize();

  const leadRanked = await fetchPercentileFromLeadRankedView(locationName, geofips);
  if (leadRanked != null) return leadRanked;

  const leadComputed = await fetchPercentileFromLeadAllFrameworksComputed(locationName, geofips);
  if (leadComputed != null) return leadComputed;

  const dedicated = await fetchFromDedicatedEnergyCostBurdenTable(locationName, geofips);
  if (dedicated != null) return dedicated;

  const livability = await fetchHousingBurdenPercentileFromLivability(locationName, geofips);
  if (livability != null) return livability;

  return fetchHousingBurdenPercentileFromCalEnviro(locationName, geofips);
}
